import { FC, useState } from 'react';
import { IonSkeletonText } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../support/HistoryViewModel';
import {
    filterAlerts,
    filterBaselines,
    IAgencyDigest,
    IInsightsFilter,
    isFiltered,
    IOutcomeMetric,
    observationsInWeek,
    ParticipationList,
    monthDay
} from '@innerhive-internal/innerhive-common';
import { ClientWellbeingModal } from './insights/ClientWellbeingModal';
import { useAgencyInsights } from './insights/useAgencyInsights';
import { UnreadableClients } from './insights/UnreadableClients';
import { NothingToChart } from './insights/NothingToChart';
import { useInsightsFilter } from './insights/useInsightsFilter';
import { IInsightsTile, InsightsTiles } from './insights/InsightsTiles';
import { InsightsFilterBar } from './insights/InsightsFilterBar';
import { InsightsDemoBanner } from './insights/InsightsDemoBanner';
import { IInsightsLens, InsightsLensSwitch } from './insights/InsightsLensSwitch';
import { InsightsFocusLens } from './insights/InsightsFocusLens';
import { InsightsValueLens } from './insights/InsightsValueLens';
import { IInsightsClientList, InsightsClientListModal } from './insights/InsightsClientListModal';
import { BeeChatViewModel } from '../helper-bee/BeeChatViewModel';

/** How many findings the Recent Changes card lists before it stops. */
const MAX_FOCUS_ALERTS = 8;

const useStyles = createUseStyles({
    page: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 18
    },

    loading: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12
    }
});

const buildTiles = (params: {
    digest: IAgencyDigest;
    hasHistory: boolean;
    filter: IInsightsFilter;
    codeStatusOpen: boolean;
    nothingReadable: boolean;
    onClearFilter: () => void;
    onToggleStanding: () => void;
    onToggleIncidents: () => void;
    onShowCodeStatusGaps: () => void;
}): IInsightsTile[] => {
    const {
        digest, hasHistory, filter, codeStatusOpen, nothingReadable,
        onClearFilter, onToggleStanding, onToggleIncidents, onShowCodeStatusGaps
    } = params;

    // Naming the book while counting part of it is how a reader comes away with the wrong caseload
    // size, and the tile is the first thing read. It counts the clients whose records could be read.
    const clientsTile = (onClick?: () => void): IInsightsTile => ({
        id: 'clients',
        value: digest.clientCount,
        label: digest.unreadableCount > 0 ? 'Clients Read' : 'Clients on the Caseload',
        isOn: Boolean(onClick) && !isFiltered(filter),
        onClick
    });

    // Only the client count survives a caseload nobody could read. The other three are zeros over
    // an empty set, which read as results rather than as the absence of any, and they are controls:
    // filtering by a standing would narrow a page with no lens on it, and the coverage tile would
    // light up pointing at a panel that is not rendered. Without its own control the count is a
    // readout, which is all it can be when there is nothing to clear a filter from.
    if (nothingReadable) {
        return [clientsTile()];
    }

    const tiles: IInsightsTile[] = [
        clientsTile(onClearFilter),
        {
            id: 'off-baseline',
            value: digest.baselines.filter(baseline => baseline.standing === 'above').length,
            label: 'Above Their Own Baseline',
            isOn: filter.standing === 'above',
            onClick: onToggleStanding
        },
        {
            id: 'incidents',
            value: digest.alerts.filter(alert => alert.kind === 'incident').length,
            label: 'Incidents or ER Visits',
            isOn: filter.kind === 'incident',
            onClick: onToggleIncidents
        },
        {
            // Counted from coverage rather than from the findings, which are window-filtered: a code
            // status missing for six months is still missing. The other three tiles narrow the
            // findings; this one counts something no finding describes, so rather than pretend to
            // filter by it, it opens the meter that can name the clients behind the number.
            id: 'code-status',
            value: digest.coverage.clientCount - digest.coverage.codeStatusConfirmed,
            label: 'Code Status Not Confirmed',
            isOn: codeStatusOpen,
            expands: true,
            onClick: onShowCodeStatusGaps
        }
    ];

    // A standing compared against a baseline nobody has yet is the reading the tab withholds, and
    // this tile is a control that filters by it.
    return hasHistory ? tiles : tiles.filter(tile => tile.id !== 'off-baseline');
};

/**
 * Care intelligence for an agency: what the caseload is doing, and who to see today.
 *
 * One digest serves two readers through a lens switch rather than two pages. A dispatcher opens on
 * Dashboard and gets the honeycomb, the clients who have moved against their own baseline, and the
 * incidents. Leadership opens Trends and gets the outcome figures, family participation and the load
 * over time. Both are the same arithmetic, so a manager and a dispatcher looking at this together
 * never see two different numbers for the same thing.
 *
 * A caseload too young to chart keeps every surface that needs no comparison; the ones that do wait
 * on its history and say what for.
 */
export const OrganizationInsights: FC<{ orgId: string }> = ({ orgId }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const chat = useInstance(BeeChatViewModel);

    const [lens, setLens] = useState<IInsightsLens>('focus');
    const [openClientId, setOpenClientId] = useState<string | undefined>();
    const [clientList, setClientList] = useState<IInsightsClientList | undefined>();
    const narrowing = useInsightsFilter();
    const { filter, narrowed, openMeter } = narrowing;

    const {
        hasLoaded, nothingReadable, digest, wellbeing, outcomes, participation, historyProgress, isDemo, unreadableClients, retry
    } = useAgencyInsights(orgId);

    // The agency home lands here, so an unfinished read has to look like one. Rendering nothing left
    // the first thing an agency sees blank for as long as the caseload takes to read.
    if (!hasLoaded) {
        return (
            <div className={classes.loading} data-id="insights-loading">
                <IonSkeletonText animated style={{ '--border-radius': '11px', height: 74 }} />
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 220 }} />
                <IonSkeletonText animated style={{ '--border-radius': '12px', height: 180 }} />
            </div>
        );
    }

    const openCareMap = (careMapId: string) => history.push(`/care/${careMapId}`);

    const openWellbeing = openClientId
        ? wellbeing.find(candidate => candidate.careMapId === openClientId)
        : undefined;

    const shownAlerts = filterAlerts(digest, filter);
    const shownBaselines = filterBaselines(digest, filter);

    // Incidents and baseline changes are what the card is for, but a reader who has picked a kind has
    // asked for that kind instead, and an empty card under their own selection reads as a broken filter.
    const focusable = [...shownAlerts]
        .filter(alert => filter.kind ? true : (alert.kind === 'incident' || alert.kind === 'baseline'))
        .sort((a, b) => b.observedOn.localeCompare(a.observedOn));

    // The tile's own lit state, so what it announces and what it does cannot disagree: it reads as
    // expanded exactly when pressing it will collapse the meter.
    const codeStatusOpen = lens === 'focus' && openMeter === 'codeStatus';

    const openOutcome = (metric: IOutcomeMetric) => setClientList({
        title: metric.drillTitle,
        blurb: metric.drillBlurb,
        clients: metric.clients,
        driversLabel: metric.driversLabel,
        definition: metric.definition
    });

    const openParticipation = (which: ParticipationList) => {
        if (which === 'quiet') {
            setClientList({
                title: 'Clients With No Family Signal',
                blurb: 'Nothing on file from these families in the last seven days. Usually an onboarding gap rather than a quiet household.',
                clients: participation.quiet
            });
            return;
        }

        // The observations figure counts observations, not clients, so it opens a list that says how
        // many each family filed. Sending it to the same list as the client figures put a count of
        // eight above a list of seven people, with nothing explaining the difference.
        setClientList({
            title: which === 'signal' ? 'Who Filed Observations This Week' : 'Families Contributing This Week',
            blurb: which === 'signal'
                ? 'How many observations each family put on the record in the last seven days.'
                : 'Clients whose family put at least one observation on the record in the last seven days.',
            clients: participation.participating
        });
    };

    const openWeek = (weekStartsOn: string) => setClientList({
        title: `Week of ${monthDay(weekStartsOn)}`,
        blurb: 'Everything recorded that week, grouped by who it was written about.',
        clients: observationsInWeek({ digest, weekStartsOn, kind: filter.kind }),
        driversLabel: 'What was recorded'
    });

    const tiles = buildTiles({
        digest,
        hasHistory: historyProgress.hasEnough,
        filter,
        codeStatusOpen,
        nothingReadable,
        onClearFilter: narrowing.clear,
        onToggleStanding: () => narrowing.toggleStanding('above'),
        onToggleIncidents: () => narrowing.toggleKind('incident'),
        onShowCodeStatusGaps: () => {
            setLens('focus');
            narrowing.setMeter(codeStatusOpen ? undefined : 'codeStatus');
        }
    });

    return (
        <div className={classes.page} data-id="organization-insights" data-lens={lens} data-demo={String(isDemo)}>
            {isDemo && <InsightsDemoBanner />}

            {!nothingReadable && <InsightsLensSwitch lens={lens} windowWeeks={digest.windowWeeks} onChange={setLens} />}

            <InsightsTiles tiles={tiles} />

            {/* Directly under the totals it qualifies, so the caseload count and the clients missing
                from it are read together, and far enough above the filter bar that it cannot be
                taken for a remark about the reader's current selection. */}
            <UnreadableClients clients={unreadableClients} onRetry={retry} />

            {narrowed && (
                <InsightsFilterBar
                    filter={filter}
                    clientCount={shownBaselines.length}
                    findingCount={shownAlerts.length}
                    onSeeWho={lens === 'value' ? () => setLens('focus') : undefined}
                    onClear={narrowing.clear}
                />
            )}

            {nothingReadable && <NothingToChart />}

            {!nothingReadable && lens === 'focus' && (
                <InsightsFocusLens
                    digest={digest}
                    wellbeing={wellbeing}
                    history={historyProgress}
                    alerts={focusable.slice(0, MAX_FOCUS_ALERTS)}
                    allAlerts={focusable}
                    unlistedAlertCount={Math.max(0, focusable.length - MAX_FOCUS_ALERTS)}
                    baselines={shownBaselines}
                    matchingIds={narrowed ? shownBaselines.map(baseline => baseline.careMapId) : undefined}
                    narrowed={narrowed}
                    openMeter={openMeter}
                    onToggleMeter={narrowing.toggleMeter}
                    onOpenClient={setOpenClientId}
                />
            )}

            {!nothingReadable && lens === 'value' && (
                <InsightsValueLens
                    digest={digest}
                    participation={participation}
                    history={historyProgress}
                    outcomes={outcomes}
                    selectedKind={filter.kind}
                    onOpenOutcome={openOutcome}
                    onOpenParticipation={openParticipation}
                    onOpenWeek={openWeek}
                    onSelectKind={narrowing.toggleKind}
                    onAskBea={question => chat.open({ trackingFrom: 'Agency Insights', initialMessage: question })}
                />
            )}

            <InsightsClientListModal
                open={clientList}
                onSelectClient={careMapId => {
                    setClientList(undefined);
                    setOpenClientId(careMapId);
                }}
                onClose={() => setClientList(undefined)}
            />

            <ClientWellbeingModal
                wellbeing={openWellbeing}
                history={historyProgress}
                onOpenProfile={isDemo ? undefined : openCareMap}
                onClose={() => setOpenClientId(undefined)}
            />
        </div>
    );
};
